import PropTypes from 'prop-types'
import { useTranslation } from 'react-i18next'
import { Accordion, AccordionGroup } from '../shared/Accordion'
import StepCard from './shared/StepCard'

function EcommerceIntro({ showFAQ = true, compact = false }) {
  const { t } = useTranslation('ecommerce')

  const faqSections = [
    {
      key: 'gettingStarted',
      title: t('faq.gettingStarted.title'),
      items: [
        { key: 'feedUrl', question: t('faq.gettingStarted.feedUrl.question'), answer: t('faq.gettingStarted.feedUrl.answer') },
        { key: 'manualUpload', question: t('faq.gettingStarted.manualUpload.question'), answer: t('faq.gettingStarted.manualUpload.answer') },
        { key: 'syncFrequency', question: t('faq.gettingStarted.syncFrequency.question'), answer: t('faq.gettingStarted.syncFrequency.answer') },
      ],
    },
    {
      key: 'videoCreation',
      title: t('faq.videoCreation.title'),
      items: [
        { key: 'productSelection', question: t('faq.videoCreation.productSelection.question'), answer: t('faq.videoCreation.productSelection.answer') },
        { key: 'designCustomization', question: t('faq.videoCreation.designCustomization.question'), answer: t('faq.videoCreation.designCustomization.answer') },
        { key: 'videoLength', question: t('faq.videoCreation.videoLength.question'), answer: t('faq.videoCreation.videoLength.answer') },
        { key: 'priceDisplay', question: t('faq.videoCreation.priceDisplay.question'), answer: t('faq.videoCreation.priceDisplay.answer') },
      ],
    },
    {
      key: 'publishing',
      title: t('faq.publishing.title'),
      items: [
        { key: 'autoPost', question: t('faq.publishing.autoPost.question'), answer: t('faq.publishing.autoPost.answer') },
        { key: 'linkInBio', question: t('faq.publishing.linkInBio.question'), answer: t('faq.publishing.linkInBio.answer') },
        { key: 'analytics', question: t('faq.publishing.analytics.question'), answer: t('faq.publishing.analytics.answer') },
      ],
    },
    {
      key: 'requirements',
      title: t('faq.requirements.title'),
      items: [
        { key: 'shopProvider', question: t('faq.requirements.shopProvider.question'), answer: t('faq.requirements.shopProvider.answer') },
        { key: 'smallCatalog', question: t('faq.requirements.smallCatalog.question'), answer: t('faq.requirements.smallCatalog.answer') },
        { key: 'pricing', question: t('faq.requirements.pricing.question'), answer: t('faq.requirements.pricing.answer') },
      ],
    },
  ]

  return (
    <div className="space-y-12">
      {/* Intro Section */}
      <section className={compact ? 'space-y-4' : 'space-y-6'}>
        <div className="text-center max-w-3xl mx-auto">
          <h1 className={`font-bold text-gray-900 ${compact ? 'text-2xl' : 'text-3xl md:text-4xl'}`}>
            {t('intro.headline')}
          </h1>
          <p className={`mt-4 text-gray-600 ${compact ? 'text-base' : 'text-lg'}`}>
            {t('intro.description')}
          </p>
          <p className={`mt-4 text-gray-700 ${compact ? 'text-sm' : 'text-base'}`}>
            {t('intro.valueProposition')}
          </p>
        </div>

        {/* How it works */}
        <div className={`bg-gradient-to-br from-primary-50 to-white rounded-2xl ${compact ? 'p-6' : 'p-8 md:p-12'}`}>
          <h2 className={`font-bold text-gray-900 mb-8 text-center ${compact ? 'text-xl' : 'text-2xl'}`}>
            {t('intro.howItWorks.title')}
          </h2>

          <div className="max-w-2xl mx-auto">
            <StepCard
              number="1"
              title={t('intro.howItWorks.step1.title')}
              description={t('intro.howItWorks.step1.description')}
            />
            <StepCard
              number="2"
              title={t('intro.howItWorks.step2.title')}
              description={t('intro.howItWorks.step2.description')}
            />
            <StepCard
              number="3"
              title={t('intro.howItWorks.step3.title')}
              description={t('intro.howItWorks.step3.description')}
            />
          </div>

          <div className="mt-8 text-center">
            <p className="inline-block bg-white px-6 py-3 rounded-full text-gray-700 font-medium shadow-sm border border-gray-100">
              {t('intro.result')}
            </p>
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      {showFAQ && (
        <section>
          <h2 className={`font-bold text-gray-900 mb-8 text-center ${compact ? 'text-xl' : 'text-2xl'}`}>
            {t('faq.title')}
          </h2>

          <div className="max-w-3xl mx-auto bg-gray-50 rounded-2xl p-8">
            <div className="space-y-8">
              {faqSections.map((section) => (
                <div key={section.key}>
                  <h3 className="text-lg font-semibold text-gray-900 mb-4">{section.title}</h3>
                  <AccordionGroup exclusive className="space-y-3">
                    {section.items.map((item) => (
                      <Accordion key={item.key} title={item.question} variant="neutral">
                        <p className="text-gray-600 text-sm leading-relaxed">{item.answer}</p>
                      </Accordion>
                    ))}
                  </AccordionGroup>
                </div>
              ))}
            </div>
          </div>
        </section>
      )}
    </div>
  )
}

EcommerceIntro.propTypes = {
  showFAQ: PropTypes.bool,
  compact: PropTypes.bool,
}

export default EcommerceIntro
